import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { apiClient } from '../api/client'

interface Member {
  id: string
  email: string
  name: string | null
  phone: string | null
  address: string | null
  membershipType: string
  status: string
  annualFee: number | null
  feeDueMonth: number | null
  totalPaidAllTime: number | null
  totalOutstanding: number | null
  createdAt: string
  payment_summary?: {
    pending_count: number
    overdue_count: number
    paid_count: number
    total_pending_cents: number
    total_overdue_cents: number
    total_outstanding_cents: number
  }
}

interface MemberFee {
  id: string
  name: string
  description: string | null
  amount: number
  feeType: string
  dueDate: string
  isActive: boolean
}

interface PaymentSummary {
  members: {
    totalMembers: number
    activeMembers: number
    totalPaid: number | null
    totalOutstanding: number | null
  }
  fees: {
    totalAssignments: number
    paidCount: number | null
    pendingCount: number | null
    overdueCount: number | null
    totalAssigned: number | null
    totalPaid: number | null
  }
  collection_rate: number
}

export default function MembersPage() {
  const { t } = useTranslation()
  const { activeOrg: currentOrganization } = useAuthStore()
  const [activeTab, setActiveTab] = useState<'members' | 'fees' | 'summary'>('members')
  const [members, setMembers] = useState<Member[]>([])
  const [fees, setFees] = useState<MemberFee[]>([])
  const [summary, setSummary] = useState<PaymentSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState('active')
  const [filterPayment, setFilterPayment] = useState('all')

  // Modals
  const [showAddMember, setShowAddMember] = useState(false)
  const [showAddFee, setShowAddFee] = useState(false)
  const [_selectedMember, setSelectedMember] = useState<Member | null>(null)

  const navigate = useNavigate()

  useEffect(() => {
    if (currentOrganization) {
      fetchMembers()
      fetchFees()
      fetchSummary()
    }
  }, [currentOrganization, filterStatus, filterPayment])

  const fetchMembers = async () => {
    if (!currentOrganization) return
    setLoading(true)
    try {
      const data = await apiClient.get<{ members: Member[] }>(
        `/members?organization_id=${currentOrganization.id}&status=${filterStatus}&payment_status=${filterPayment}`
      )
      if (data.members) {
        setMembers(data.members)
      }
    } catch {
      setError('Failed to load members')
    } finally {
      setLoading(false)
    }
  }

  const fetchFees = async () => {
    if (!currentOrganization) return
    try {
      const data = await apiClient.get<{ fees: MemberFee[] }>(
        `/members/fees?organization_id=${currentOrganization.id}`
      )
      if (data.fees) {
        setFees(data.fees)
      }
    } catch {
      console.error('Failed to load fees')
    }
  }

  const fetchSummary = async () => {
    if (!currentOrganization) return
    try {
      const data = await apiClient.get<{ summary: PaymentSummary }>(
        `/members/payment-summary?organization_id=${currentOrganization.id}`
      )
      if (data.summary) {
        setSummary(data.summary)
      }
    } catch {
      console.error('Failed to load summary')
    }
  }

  const formatCents = (cents: number | null) => {
    if (cents === null) return '0,00 €'
    return `${(cents / 100).toFixed(2).replace('.', ',')} €`
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fi-FI')
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">👥 Jäsenrekisteri</h1>
          <p className="text-gray-600">Jäsenten hallinta ja jäsenmaksujen seuranta</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/members/dues')}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            💶 Maksujen seuranta
          </button>
          <button
            onClick={() => setShowAddMember(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            + {t('members.addMember')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200 mb-6">
        {[
          { key: 'members', label: 'Jäsenet', icon: '👥' },
          { key: 'fees', label: 'Jäsenmaksut', icon: '💶' },
          { key: 'summary', label: 'Yhteenveto', icon: '📊' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`pb-3 px-4 border-b-2 transition ${
              activeTab === tab.key
                ? 'border-indigo-600 text-indigo-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">{error}</div>
      )}

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div>
          <div className="flex gap-4 mb-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="active">Aktiiviset</option>
              <option value="inactive">Passiiviset</option>
              <option value="suspended">Keskeytetyt</option>
              <option value="all">Kaikki</option>
            </select>
            <select
              value={filterPayment}
              onChange={(e) => setFilterPayment(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="all">Kaikki maksut</option>
              <option value="paid">Maksaneet</option>
              <option value="unpaid">Maksamatta</option>
              <option value="overdue">Myöhässä</option>
            </select>
          </div>

          {loading ? (
            <div className="text-center py-8">Ladataan...</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nimi</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sähköposti</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tyyppi</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Vuosimaksu</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Maksamatta</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Toiminnot</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {members.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{member.name || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{member.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          member.membershipType === 'premium' ? 'bg-purple-100 text-purple-800' :
                          member.membershipType === 'honorary' ? 'bg-yellow-100 text-yellow-800' :
                          member.membershipType === 'student' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {member.membershipType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">{formatCents(member.annualFee)}</td>
                      <td className="px-4 py-3 text-right">
                        {member.payment_summary?.total_outstanding_cents ? (
                          <span className="text-red-600 font-medium">
                            {formatCents(member.payment_summary.total_outstanding_cents)}
                          </span>
                        ) : (
                          <span className="text-green-600">OK</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => {
                            setSelectedMember(member)
                          }}
                          className="text-indigo-600 hover:text-indigo-800 text-sm mr-2"
                        >
                          Lisää maksu
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {members.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Ei jäseniä. Lisää ensimmäinen jäsen "Lisää jäsen" -napista.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Fees Tab */}
      {activeTab === 'fees' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Jäsenmaksumääritykset</h2>
            <button
              onClick={() => setShowAddFee(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              + Uusi maksu
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nimi</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tyyppi</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Summa</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Eräpäivä</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tila</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {fees.map((fee) => (
                  <tr key={fee.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{fee.name}</div>
                      {fee.description && (
                        <div className="text-sm text-gray-500">{fee.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {fee.feeType === 'annual' ? 'Vuosimaksu' :
                       fee.feeType === 'quarterly' ? 'Neljännesvuosimaksu' :
                       fee.feeType === 'monthly' ? 'Kuukausimaksu' : 'Kertamaksu'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{formatCents(fee.amount)}</td>
                    <td className="px-4 py-3">{formatDate(fee.dueDate)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        fee.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {fee.isActive ? 'Aktiivinen' : 'Ei aktiivinen'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {fees.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Ei maksumäärityksiä. Lisää ensimmäinen maksu "Uusi maksu" -napista.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Tab */}
      {activeTab === 'summary' && summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-500 mb-1">Jäsenet yhteensä</div>
            <div className="text-3xl font-bold text-gray-800">{summary.members.totalMembers}</div>
            <div className="text-sm text-gray-500">
              {summary.members.activeMembers} aktiivista
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-500 mb-1">Kokonaiskertymä</div>
            <div className="text-3xl font-bold text-green-600">
              {formatCents(summary.members.totalPaid)}
            </div>
            <div className="text-sm text-gray-500">Kaikki maksut yhteensä</div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-500 mb-1">Maksamatta</div>
            <div className="text-3xl font-bold text-red-600">
              {formatCents(summary.members.totalOutstanding)}
            </div>
            <div className="text-sm text-gray-500">
              {summary.fees.overdueCount || 0} myöhässä
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-500 mb-1">Kantasuhte</div>
            <div className="text-3xl font-bold text-indigo-600">{summary.collection_rate}%</div>
            <div className="text-sm text-gray-500">
              {summary.fees.paidCount || 0} / {summary.fees.totalAssignments} maksettu
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow md:col-span-2">
            <h3 className="font-semibold mb-4">Maksutilanne</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Maksettu</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500"
                      style={{
                        width: `${summary.fees.totalAssignments > 0
                          ? ((summary.fees.paidCount || 0) / summary.fees.totalAssignments) * 100
                          : 0}%`
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium w-12 text-right">{summary.fees.paidCount || 0}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Odottaa</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-500"
                      style={{
                        width: `${summary.fees.totalAssignments > 0
                          ? ((summary.fees.pendingCount || 0) / summary.fees.totalAssignments) * 100
                          : 0}%`
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium w-12 text-right">{summary.fees.pendingCount || 0}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Myöhässä</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-500"
                      style={{
                        width: `${summary.fees.totalAssignments > 0
                          ? ((summary.fees.overdueCount || 0) / summary.fees.totalAssignments) * 100
                          : 0}%`
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium w-12 text-right">{summary.fees.overdueCount || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMember && (
        <AddMemberModal
          organizationId={currentOrganization?.id || ''}
          onClose={() => setShowAddMember(false)}
          onSuccess={() => {
            setShowAddMember(false)
            fetchMembers()
          }}
        />
      )}

      {/* Add Fee Modal */}
      {showAddFee && (
        <AddFeeModal
          organizationId={currentOrganization?.id || ''}
          onClose={() => setShowAddFee(false)}
          onSuccess={() => {
            setShowAddFee(false)
            fetchFees()
          }}
        />
      )}
    </div>
  )
}

// Modal Components

function AddMemberModal({ organizationId, onClose, onSuccess }: {
  organizationId: string
  onClose: () => void
  onSuccess: () => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    email: '',
    name: '',
    phone: '',
    address: '',
    membership_type: 'regular',
    annual_fee: '',
    fee_due_month: '1',
    notes: '',
  })
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await apiClient.post('/members', {
        ...form,
        organization_id: organizationId,
        annual_fee: form.annual_fee ? parseInt(form.annual_fee) * 100 : 0,
        fee_due_month: parseInt(form.fee_due_month),
      })
      onSuccess()
    } catch {
      alert('Failed to add member')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Lisää uusi jäsen</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sähköposti *</label>
            <input
              type="email"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nimi</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Puhelin</label>
            <input
              type="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Osoite</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={2}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jäsenyystyyppi</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.membership_type}
              onChange={(e) => setForm({ ...form, membership_type: e.target.value })}
            >
              <option value="regular">Tavallinen</option>
              <option value="premium">Premium</option>
              <option value="honorary">Kunniajäsen</option>
              <option value="student">Opiskelija</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vuosimaksu (€)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.annual_fee}
              onChange={(e) => setForm({ ...form, annual_fee: e.target.value })}
              placeholder="0,00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Maksukuukausi</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.fee_due_month}
              onChange={(e) => setForm({ ...form, fee_due_month: e.target.value })}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2000, i, 1).toLocaleString('fi-FI', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Muistiinpanot</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Peruuta
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? t('common.adding') : t('members.addMember')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function AddFeeModal({ organizationId, onClose, onSuccess }: {
  organizationId: string
  onClose: () => void
  onSuccess: () => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    name: '',
    description: '',
    amount: '',
    fee_type: 'annual',
    due_date: new Date().toISOString().split('T')[0],
    grace_period_days: '30',
  })
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await apiClient.post('/members/fees', {
        ...form,
        organization_id: organizationId,
        amount: parseFloat(form.amount) * 100,
        grace_period_days: parseInt(form.grace_period_days),
        late_fee_amount: 0,
      })
      onSuccess()
    } catch {
      alert('Failed to create fee')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Uusi jäsenmaksu</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nimi *</label>
            <input
              type="text"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="esim. Vuosimaksu 2026"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kuvaus</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Summa (€) *</label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="0,00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Maksutyyppi</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.fee_type}
              onChange={(e) => setForm({ ...form, fee_type: e.target.value })}
            >
              <option value="annual">Vuosimaksu</option>
              <option value="quarterly">Neljännesvuosimaksu</option>
              <option value="monthly">Kuukausimaksu</option>
              <option value="one_time">Kertamaksu</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Eräpäivä *</label>
            <input
              type="date"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Maksuaika (päivää)</label>
            <input
              type="number"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.grace_period_days}
              onChange={(e) => setForm({ ...form, grace_period_days: e.target.value })}
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Peruuta
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? t('common.creating') : t('members.createPayment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
